import { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Link2, Loader2, Share2, Search, UserCheck, Building2, Users, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatApiErrorDetail } from "@/lib/api";
import { formatPatientDisplayUhid } from "@/lib/patientDisplay";
import { ROLE_LABELS } from "@/lib/rbac";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getOrgLabel = (org) => org?.name ?? org?.organization_name ?? org?.label ?? "";
const getOrgId = (org) => String(org?.id ?? org?.organization_id ?? "");

const hasSharedAt = (value) => {
  if (value === null || value === undefined) return false;
  return String(value).trim() !== "";
};

export default function SharePatientDialog({ open, onOpenChange, patient }) {
  const { user: authUser } = useAuth();

  const [recipientEmail, setRecipientEmail] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("7");
  const [linkBusy, setLinkBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);

  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [users, setUsers] = useState([]);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);
  const [revokingUserId, setRevokingUserId] = useState(null);

  const patientUhid = patient?.uhid;

  const fetchOrganizations = useCallback(async () => {
    setOrgsLoading(true);
    try {
      const res = await axios.get(`${API}/organizations`, {
        params: { page: 1, limit: 100 },
      });
      setOrganizations(res.data?.organizations ?? res.data?.items ?? []);
    } catch {
      toast.error("Failed to load organizations");
      setOrganizations([]);
    } finally {
      setOrgsLoading(false);
    }
  }, []);

  const fetchSharedUsers = useCallback(async () => {
    if (!patientUhid) return;
    try {
      const res = await axios.get(`${API}/patients/${encodeURIComponent(patientUhid)}/share/users`, {
        params: { filter: "with_access", limit: 100 },
      });
      const shared = (res.data?.users || []).filter((u) => hasSharedAt(u.shared_at));
      setSharedUsers(shared);
    } catch {
      setSharedUsers([]);
    }
  }, [patientUhid]);

  const fetchUsers = useCallback(async () => {
    if (!patientUhid || !selectedOrganizationId) {
      setUsers([]);
      return;
    }
    setUsersLoading(true);
    try {
      const res = await axios.get(`${API}/patients/${encodeURIComponent(patientUhid)}/share/users`, {
        params: {
          search: debouncedSearch.trim() || undefined,
          filter: "all",
          organization_id: selectedOrganizationId,
          page: 1,
          limit: 50,
        },
      });
      setUsers(res.data?.users || []);
    } catch {
      toast.error("Failed to load users");
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [patientUhid, selectedOrganizationId, debouncedSearch]);

  const sharedUserMap = useMemo(
    () => new Map(sharedUsers.map((u) => [u.id, u])),
    [sharedUsers],
  );

  const filteredUsers = useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    const annotated = users
      .filter((u) => {
        if (!authUser) return true;
        if (authUser.id && u.id === authUser.id) return false;
        if (
          authUser.email &&
          u.email?.toLowerCase() === authUser.email.toLowerCase()
        ) {
          return false;
        }
        return true;
      })
      .map((u) => {
        const shared = sharedUserMap.get(u.id);
        return {
          ...u,
          has_access: shared?.has_access ?? u.has_access,
          shared_at: shared?.shared_at ?? u.shared_at ?? null,
        };
      });
    if (!query) return annotated;
    return annotated.filter(
      (u) =>
        u.name?.toLowerCase().includes(query) ||
        u.email?.toLowerCase().includes(query),
    );
  }, [users, userSearch, sharedUserMap, authUser]);

  useEffect(() => {
    if (!open) return;
    setRecipientEmail("");
    setExpiresInDays("7");
    setShareUrl(null);
    setLinkBusy(false);
    setSelectedOrganizationId("");
    setUserSearch("");
    setDebouncedSearch("");
    setSelectedUser(null);
    setUsers([]);
    setSharedUsers([]);
    setShareBusy(false);
    setRevokingUserId(null);

    fetchOrganizations();
    if (patientUhid) fetchSharedUsers();
  }, [open, patientUhid, fetchOrganizations, fetchSharedUsers]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => setDebouncedSearch(userSearch), 300);
    return () => clearTimeout(timer);
  }, [open, userSearch]);

  useEffect(() => {
    if (!open) return;
    setSelectedUser(null);
    fetchUsers();
  }, [open, selectedOrganizationId, debouncedSearch, fetchUsers]);

  const handleCreateLink = async () => {
    if (!patientUhid) return;
    setLinkBusy(true);
    try {
      const payload = {
        entity_type: "patient",
        entity_id: patientUhid,
        permissions: ["view"],
        guest_access: true,
        expires_in_days: Number(expiresInDays) || 7,
      };
      if (recipientEmail.trim()) payload.recipient_email = recipientEmail.trim();
      const res = await axios.post(`${API}/shares`, payload);
      const token = res.data?.token;
      if (!token) throw new Error("Share link created but no token returned");
      setShareUrl(`${window.location.origin}/shared/${token}`);
      toast.success("Share link created");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create share link");
    }
    setLinkBusy(false);
  };

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const handleShareWithUser = async () => {
    const userId = selectedUser?.id;
    if (!patientUhid) {
      toast.error("Patient not found");
      return;
    }
    if (!userId) {
      toast.error("Select a user to share with");
      return;
    }

    setShareBusy(true);
    try {
      await axios.post(
        `${API}/patients/${encodeURIComponent(patientUhid)}/share`,
        { user_id: userId },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success(`Shared with ${selectedUser.name}`);
      setSharedUsers((prev) => {
        const exists = prev.some((u) => u.id === userId);
        const sharedAt = new Date().toISOString();
        if (exists) {
          return prev.map((u) => (
            u.id === userId ? { ...u, has_access: true, shared_at: sharedAt } : u
          ));
        }
        return [...prev, { ...selectedUser, has_access: true, shared_at: sharedAt }];
      });
      setSelectedUser(null);
      fetchSharedUsers();
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to share with user");
    } finally {
      setShareBusy(false);
    }
  };

  const handleRevokeAccess = async (user) => {
    const userId = user?.id;
    if (!patientUhid || !userId) return;

    setRevokingUserId(userId);
    try {
      await axios.delete(
        `${API}/patients/${encodeURIComponent(patientUhid)}/share/${encodeURIComponent(userId)}`,
      );
      toast.success(`Revoked access for ${user.name}`);
      setSharedUsers((prev) => prev.filter((u) => u.id !== userId));
      if (selectedUser?.id === userId) setSelectedUser(null);
      fetchSharedUsers();
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to revoke access");
    } finally {
      setRevokingUserId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="share-patient-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-4 h-4 text-[var(--xl-primary)]" />
            Share Patient
          </DialogTitle>
          <DialogDescription>
            Share via a guest link or directly with a registered user.
          </DialogDescription>
        </DialogHeader>

        {patient && (
          <div className="rounded-lg border border-border bg-secondary/20 px-3 py-2">
            <p className="text-sm font-medium truncate" title={patient.name}>
              {patient.name || "Unnamed patient"}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5 font-mono">
              {formatPatientDisplayUhid(patient)}
            </p>
          </div>
        )}

        <Tabs defaultValue="link">
          <TabsList className="w-full">
            <TabsTrigger value="link" className="flex-1 gap-1.5">
              <Link2 className="w-3.5 h-3.5" />
              Guest Link
            </TabsTrigger>
            <TabsTrigger value="user" className="flex-1 gap-1.5">
              <Users className="w-3.5 h-3.5" />
              Share with User
            </TabsTrigger>
          </TabsList>

          <TabsContent value="link" className="mt-4 space-y-4">
            {!shareUrl ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="share-patient-recipient-email">Recipient email (optional)</Label>
                  <Input
                    id="share-patient-recipient-email"
                    type="email"
                    placeholder="someone@example.com"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    data-testid="share-patient-recipient-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="share-patient-expiry">Link expires in</Label>
                  <Select value={expiresInDays} onValueChange={setExpiresInDays}>
                    <SelectTrigger id="share-patient-expiry" data-testid="share-patient-expiry-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 day</SelectItem>
                      <SelectItem value="3">3 days</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  className="w-full gap-2"
                  onClick={handleCreateLink}
                  disabled={linkBusy || !patientUhid}
                  data-testid="create-patient-share-link-btn"
                >
                  {linkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                  Create Link
                </Button>
              </>
            ) : (
              <div className="space-y-2">
                <Label>Shareable link</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={shareUrl}
                    className="font-mono text-xs"
                    data-testid="patient-share-link-output"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleCopyLink}
                    aria-label="Copy share link"
                    data-testid="copy-patient-share-link-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Link2 className="w-3 h-3" />
                  Anyone with this link can view this patient and their studies until it expires.
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="user" className="mt-4 space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Organization</Label>
            <Select
              value={selectedOrganizationId || undefined}
              onValueChange={setSelectedOrganizationId}
              disabled={orgsLoading}
            >
              <SelectTrigger data-testid="share-patient-org-select">
                <SelectValue placeholder={orgsLoading ? "Loading organizations…" : "Select organization"} />
              </SelectTrigger>
              <SelectContent
                side="bottom"
                align="start"
                sideOffset={4}
                avoidCollisions={false}
                className="max-h-60"
              >
                {organizations.length === 0 && !orgsLoading ? (
                  <SelectItem value="__none__" disabled>
                    No organizations found
                  </SelectItem>
                ) : (
                  organizations.map((org) => {
                    const orgId = getOrgId(org);
                    const label = getOrgLabel(org) || orgId;
                    if (!orgId) return null;
                    return (
                      <SelectItem key={orgId} value={orgId}>
                        {label}
                      </SelectItem>
                    );
                  })
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <Input
              placeholder={selectedOrganizationId ? "Search by name or email…" : "Select an organization first"}
              className="pl-8 text-sm"
              value={userSearch}
              disabled={!selectedOrganizationId}
              onChange={(e) => {
                setUserSearch(e.target.value);
                setSelectedUser(null);
              }}
              data-testid="share-patient-user-search"
            />
          </div>

          {!selectedOrganizationId ? (
            <div className="flex flex-col items-center justify-center py-8 text-center rounded-md border border-dashed border-border">
              <Building2 className="w-5 h-5 text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">Select an organization to view its users</p>
            </div>
          ) : usersLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <ScrollArea className="h-64 sm:h-72 rounded-md border border-border">
              {filteredUsers.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground py-6">No users found</p>
              ) : (
                <div className="p-1">
                  {filteredUsers.map((u) => {
                    const isSelected = selectedUser?.id === u.id;
                    const canShare = u.has_access !== true;
                    const canRevoke = hasSharedAt(u.shared_at);
                    const isRevoking = revokingUserId === u.id;
                    return (
                      <div
                        key={u.id}
                        className={`flex items-center gap-2 px-2 py-1 rounded-md ${
                          isSelected ? "bg-primary/10 border border-primary/30" : ""
                        }`}
                      >
                        <button
                          type="button"
                          disabled={!canShare || isRevoking}
                          onClick={() => setSelectedUser(isSelected ? null : u)}
                          className={`flex-1 flex items-center gap-3 px-1 py-1.5 rounded-md text-left transition-colors min-w-0 ${
                            !canShare
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:bg-muted/50"
                          }`}
                        >
                          <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center flex-shrink-0 text-[11px] font-semibold text-muted-foreground uppercase">
                            {u.name?.charAt(0) ?? "?"}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{u.name}</p>
                            <p className="text-[11px] text-muted-foreground truncate">{u.email}</p>
                          </div>
                          {canRevoke ? (
                            <UserCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                          ) : isSelected ? (
                            <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                          ) : null}
                          {u.role && (
                            <Badge variant="outline" className="text-[10px] flex-shrink-0">
                              {ROLE_LABELS[u.role] ?? u.role.replace(/_/g, " ")}
                            </Badge>
                          )}
                        </button>
                        {canRevoke && (
                          <button
                            type="button"
                            onClick={() => handleRevokeAccess(u)}
                            disabled={isRevoking || shareBusy}
                            className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive disabled:opacity-50 flex-shrink-0"
                            aria-label={`Revoke access for ${u.name}`}
                            data-testid={`revoke-patient-share-${u.id}`}
                          >
                            {isRevoking ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <X className="w-3.5 h-3.5" />
                            )}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          )}

          {sharedUsers.length > 0 && (
            <p className="text-[11px] text-muted-foreground">
              Shared with: {sharedUsers.map((u) => u.name).join(", ")}
            </p>
          )}

          <Button
            className="w-full gap-2"
            onClick={handleShareWithUser}
            disabled={shareBusy || !selectedUser?.id || !patientUhid || !selectedOrganizationId}
            data-testid="share-patient-with-user-btn"
          >
            {shareBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            {selectedUser ? `Share with ${selectedUser.name}` : "Select a user"}
          </Button>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {shareUrl ? "Done" : "Close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
